import { frameTimeSeconds, type FrameSchedule } from "../../core/animation/frameSchedule";
import type { AlterlyEffectInstance } from "../../core/effects/effectRegistry";
import { assertExportMemoryBudget } from "../../core/export/exportSafety";
import type { MediaAsset } from "../../core/media/mediaTypes";
import { renderEffectStack } from "../../engine/cpu/renderEffectStack";
import { AppError } from "../../shared/errors/AppError";
import type { FrameProvider } from "./WebCodecsVideoEncoder";

export type RenderedVideoFrameProvider = {
  frameProvider: FrameProvider;
  dispose: () => void;
};

function waitForEvent(target: HTMLMediaElement, eventName: "loadeddata" | "seeked", signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      target.removeEventListener(eventName, complete);
      target.removeEventListener("error", fail);
      signal.removeEventListener("abort", abort);
    };
    const complete = () => {
      cleanup();
      resolve();
    };
    const fail = () => {
      cleanup();
      reject(new AppError("DECODE_FAILED", `Video ${eventName} failed during export.`));
    };
    const abort = () => {
      cleanup();
      reject(signal.reason ?? new DOMException("Video export cancelled.", "AbortError"));
    };

    target.addEventListener(eventName, complete, { once: true });
    target.addEventListener("error", fail, { once: true });
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
  });
}

export function createRenderedVideoFrameProvider(
  asset: MediaAsset,
  effects: readonly AlterlyEffectInstance[],
  schedule: FrameSchedule,
): RenderedVideoFrameProvider {
  if (asset.kind !== "video") {
    throw new AppError("EXPORT_FAILED", "A video source is required for video export.");
  }

  assertExportMemoryBudget(
    asset.dimensions.width,
    asset.dimensions.height,
    Math.max(3, effects.length + 2),
  );

  const video = document.createElement("video");
  video.src = asset.objectUrl;
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;

  const canvas = document.createElement("canvas");
  canvas.width = asset.dimensions.width;
  canvas.height = asset.dimensions.height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new AppError("EXPORT_FAILED", "Unable to create the export rendering canvas.");

  let initialized = false;

  const ensureReady = async (signal: AbortSignal) => {
    if (initialized && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return;
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      video.load();
      await waitForEvent(video, "loadeddata", signal);
    }
    initialized = true;
  };

  const frameProvider: FrameProvider = async (frameIndex, signal) => {
    await ensureReady(signal);
    const requested = frameTimeSeconds(schedule, frameIndex);
    const duration = Number.isFinite(video.duration) ? video.duration : schedule.durationSeconds;
    const safeEnd = Math.max(0, duration - 0.000001);
    const time = Math.min(requested, safeEnd);

    if (Math.abs(video.currentTime - time) > 0.000001) {
      const seek = waitForEvent(video, "seeked", signal);
      video.currentTime = time;
      await seek;
    }

    if (signal.aborted) {
      throw signal.reason ?? new DOMException("Video export cancelled.", "AbortError");
    }

    renderEffectStack(
      context,
      video,
      asset.dimensions.width,
      asset.dimensions.height,
      effects,
    );
    return canvas;
  };

  return {
    frameProvider,
    dispose: () => {
      video.pause();
      video.removeAttribute("src");
      video.load();
      canvas.width = 1;
      canvas.height = 1;
    },
  };
}
